import * as React from "react";
import { cn } from "@/lib/utils";
import type { ResizeEdge } from "@/lib/useResizableDimensions";

export type ResizeHandlesProps = {
  onResizeStart: (edge: ResizeEdge, event: React.PointerEvent) => void;
  disabled?: boolean;
};

const HANDLE_SIZE = 8;
const CORNER_SIZE = 12;

const cursorMap: Record<ResizeEdge, string> = {
  n: "cursor-ns-resize",
  s: "cursor-ns-resize",
  e: "cursor-ew-resize",
  w: "cursor-ew-resize",
  ne: "cursor-nesw-resize",
  nw: "cursor-nwse-resize",
  se: "cursor-nwse-resize",
  sw: "cursor-nesw-resize",
};

type HandleConfig = {
  edge: ResizeEdge;
  style: React.CSSProperties;
};

const handles: HandleConfig[] = [
  // Edges
  {
    edge: "n",
    style: {
      top: 0,
      left: CORNER_SIZE,
      right: CORNER_SIZE,
      height: HANDLE_SIZE,
    },
  },
  {
    edge: "s",
    style: {
      bottom: 0,
      left: CORNER_SIZE,
      right: CORNER_SIZE,
      height: HANDLE_SIZE,
    },
  },
  {
    edge: "e",
    style: {
      right: 0,
      top: CORNER_SIZE,
      bottom: CORNER_SIZE,
      width: HANDLE_SIZE,
    },
  },
  {
    edge: "w",
    style: {
      left: 0,
      top: CORNER_SIZE,
      bottom: CORNER_SIZE,
      width: HANDLE_SIZE,
    },
  },
  // Corners
  {
    edge: "nw",
    style: {
      top: 0,
      left: 0,
      width: CORNER_SIZE,
      height: CORNER_SIZE,
    },
  },
  {
    edge: "ne",
    style: {
      top: 0,
      right: 0,
      width: CORNER_SIZE,
      height: CORNER_SIZE,
    },
  },
  {
    edge: "sw",
    style: {
      bottom: 0,
      left: 0,
      width: CORNER_SIZE,
      height: CORNER_SIZE,
    },
  },
  {
    edge: "se",
    style: {
      bottom: 0,
      right: 0,
      width: CORNER_SIZE,
      height: CORNER_SIZE,
    },
  },
];

export function ResizeHandles({
  onResizeStart,
  disabled = false,
}: ResizeHandlesProps) {
  if (disabled) return null;

  return (
    <>
      {handles.map(({ edge, style }) => (
        <div
          key={edge}
          data-testid={`resize-handle-${edge}`}
          data-resize-edge={edge}
          className={cn(
            "absolute z-60 touch-none",
            cursorMap[edge]
          )}
          style={{
            ...style,
            // Transparent background, but still captures pointer events
            background: "transparent",
          }}
          onPointerDown={(e) => onResizeStart(edge, e)}
        />
      ))}
    </>
  );
}

ResizeHandles.displayName = "ResizeHandles";
